import React, { useState, useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Tabs } from 'antd';
import Button from '@src/components/button/Button';
import Select from '@src/components/select/Select';
import Spinner from '@src/components/spinner/Spinner';
import OverflowTooltip from '@src/components/overflowTooltip/OverflowTooltip';
import Checkbox from '@src/components/checkbox/Checkbox';
import { useToast, useUser } from '@src/hooks';
import { countryOptions, SURVEY_TYPE } from '@constants';
import { useQueryDictionary } from '@src/hooks/useQueryDictionary';
import { useGetFailedReportsLazyQuery, useManuallyTriggerReportMutation, useUsersLazyQuery, useGetAccountSchoolsLazyQuery } from '@src/api/graphql-codegen';
import moment from 'moment';
import styles from './ManualReportGeneration.module.scss';
import { SelectOption, SurveyTypes } from '@src/types';
import { useRolesPermissions } from '@src/hooks/useRolesPermission';
import { UserRoleEnum } from '@src/types';
import { AdminGrid, AdminTable, TableLink, ActionButtons } from '@src/components/admin-grid';
import FilterSection from '@src/components/filter-section';
import DateRangeFilter from '@src/components/filter-section/DateRangeFilter';
import { BodyColumn, HeaderColumn } from '@src/components/table/Table';
import { logger } from '../../../utils/logger';

const surveyTypeOptions = [
  { value: SurveyTypes.ExitTickets, label: 'Exit Tickets' },
  { value: SurveyTypes.PreProgram, label: 'Pre-Program Quiz' },
  { value: SurveyTypes.PostProgram, label: 'Post-Program Quiz' },
];

const getSurveyTypeId = (country: string, survey: string) => {
  if (survey === SurveyTypes.ExitTickets) {
    return SURVEY_TYPE.EXIT_TICKET;
  } else if (survey === SurveyTypes.PreProgram) {
    if (country === 'US' || country === 'CA') {
      return SURVEY_TYPE.NA_PRE_PROGRAM_QUIZ;
    } else {
      return SURVEY_TYPE.AU_PRE_PROGRAM_QUIZ;
    }
  } else if (survey === SurveyTypes.PostProgram) {
    if (country === 'US' || country === 'CA') {
      return SURVEY_TYPE.NA_POST_PROGRAM_QUIZ;
    } else {
      return SURVEY_TYPE.AU_POST_PROGRAM_QUIZ;
    }
  } else {
    return SURVEY_TYPE.EXIT_TICKET;
  }
};

const ManualReportGeneration: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'byUser' | 'byCountry'>('byUser');
  const [countryFilter, setCountryFilter] = useState('');
  const [surveyFilter, setSurveyFilter] = useState('');
  const [accountFilter, setAccountFilter] = useState('');
  const [schoolFilter, setSchoolFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [startDate, setStartDate] = useState<any>(moment(new Date()).subtract(1, 'days').format('MM.DD.YYYY'));
  const [endDate, setEndDate] = useState<any>(moment(new Date()).format('MM.DD.YYYY'));
  const [processingFormId, setProcessingFormId] = useState<string | null>(null);
  const [schoolUsers, setSchoolUsers] = useState<SelectOption[]>([]);
  const [countryOnlyFilter, setCountryOnlyFilter] = useState('');
  const [excludeZeroResponses, setExcludeZeroResponses] = useState(false);

  const { push } = useHistory();
  const [user] = useUser();
  const { successToast, warningToast, errorToast } = useToast();
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(true);
  const customersAllOptions = useQueryDictionary({ dictionary: 'Customers' });
  const schoolAllOptions = useQueryDictionary({ dictionary: 'Schools' });
  const isSuperAdmin = useRolesPermissions([UserRoleEnum.SuperAdmin]);
  
  // Set loading state for accounts
  useEffect(() => {
    if (customersAllOptions.length > 0) {
      setIsLoadingAccounts(false);
    }
  }, [customersAllOptions]);

  const [getFailedReports, { data: failedReportsData, loading: isLoadingReports, refetch }] = useGetFailedReportsLazyQuery();
  const [manuallyTriggerReport, { loading: isTriggering }] = useManuallyTriggerReportMutation();
  const [getUsers, { data: usersData, loading: isLoadingUsers }] = useUsersLazyQuery();
  const [getSchools, { data: accountSchoolsData, loading: isAccountSchoolsFetching }] = useGetAccountSchoolsLazyQuery({ fetchPolicy: 'cache-first' });

  useEffect(() => {
    if (user && !isSuperAdmin) {
      push('/dashboard');
    }
  }, [user, push, isSuperAdmin]);

  const customersOptions = React.useMemo(
    () => {
      const filtered = countryFilter
        ? customersAllOptions.filter(option => option.value.startsWith(countryFilter))
        : customersAllOptions;

      // Sort alphabetically by label
      return filtered.sort((a, b) => a.label.localeCompare(b.label));
    },
    [customersAllOptions, countryFilter]
  );

  const schoolOptions = React.useMemo(() => {
    // If we're fetching schools, return an empty array to show the loading spinner
    if (isAccountSchoolsFetching) {
      return [];
    }

    let filtered;
    if (!countryFilter && !accountFilter) {
      filtered = schoolAllOptions;
    } else if (!accountFilter && countryFilter) {
      filtered = schoolAllOptions.filter(option => option.value.startsWith(countryFilter));
    } else if (accountFilter && accountSchoolsData?.accountSchools?.schools?.length) {
      // Use the schools from the account schools data
      return (accountSchoolsData?.accountSchools?.schools || [])
        .map(option => ({ label: option?.name, value: option?.id } as SelectOption))
        .sort((a, b) => a.label.localeCompare(b.label));
    } else {
      // Filter schools by account
      // Schools belonging to an account typically have the account name in their label
      const accountName = customersOptions.find(option => option.value === accountFilter)?.label;
      filtered = schoolAllOptions.filter(option =>
        option.value.startsWith(countryFilter) &&
        (accountName ? option.label.includes(accountName) : true)
      );
    }

    // Sort alphabetically by label
    return filtered ? filtered.sort((a, b) => a.label.localeCompare(b.label)) : [];
  }, [countryFilter, accountFilter, schoolAllOptions, customersOptions, isAccountSchoolsFetching, accountSchoolsData?.accountSchools?.schools]);

  // Effect to fetch schools when account is selected
  useEffect(() => {
    if (countryFilter && accountFilter) {
      getSchools({
        variables: {
          input: {
            id: accountFilter
          }
        }
      });
    } else {
      setSchoolFilter('');
    }
  }, [countryFilter, accountFilter, getSchools]);

  // Effect to fetch users when school is selected
  useEffect(() => {
    if (countryFilter && accountFilter && schoolFilter) {
      getUsers({
        variables: {
          input: {
            country: countryFilter,
            accountId: accountFilter,
            schoolId: schoolFilter
          }
        }
      });
    } else {
      setSchoolUsers([]);
      setUserFilter('');
    }
  }, [countryFilter, accountFilter, schoolFilter, getUsers]);

  // Update school users when user data is loaded
  useEffect(() => {
    // If we're loading users, set an empty array to show the loading spinner
    if (isLoadingUsers) {
      setSchoolUsers([]);
      return;
    }

    if (usersData?.users) {
      const userOptions = usersData.users
        .filter(u => u !== null && typeof u === 'object')
        .map(u => ({
          value: u?.id || '',
          // Include user ID in the label
          label: `${u?.firstName || ''} ${u?.lastName || ''} (${u?.id || ''})`
        }))
        .filter(option => option.value && option.label.trim() !== '')
        // Sort alphabetically by label
        .sort((a, b) => a.label.localeCompare(b.label));
      setSchoolUsers(userOptions);
    }
  }, [usersData, isLoadingUsers]);

  const handleResetFilters = useCallback((skipCountry?: boolean) => {
    setAccountFilter('');
    setSchoolFilter('');
    setSurveyFilter('');
    if (!skipCountry) {
      setCountryFilter('');
    }
    setStartDate(moment(new Date()).subtract(1, 'days').format('MM.DD.YYYY'));
    setEndDate(moment(new Date()).format('MM.DD.YYYY'));
  }, []);

  const handleChangeFilter = useCallback((setter: React.Dispatch<React.SetStateAction<string>>, fieldName?: string) => (o: SelectOption) => {
    setter(o.value);
    if (fieldName === 'Country') {
      // Reset account and school filters when country changes
      setAccountFilter('');
      setSchoolFilter('');
      setUserFilter('');
    } else if (fieldName === 'Account') {
      // Reset school filter when account changes
      setSchoolFilter('');
      setUserFilter('');
    } else if (fieldName === 'School') {
      // Reset user filter when school changes
      setUserFilter('');
    }
  }, []);

  const handleChangeDate = useCallback(
    (setter: React.Dispatch<React.SetStateAction<string>>) => (date: any, dateString: string) => {
      setter(dateString);
    },
    []
  );

  const handleSearch = useCallback(() => {
    if (!countryFilter) {
      warningToast('Please select a country');
      return;
    }

    const schoolIds = schoolFilter ? [schoolFilter] : schoolAllOptions.filter(option => option.value.startsWith(countryFilter)).map(option => option.value);

    if (schoolIds.length === 0) {
      warningToast('No schools found for the selected filters');
      return;
    }

    const surveyType = surveyFilter ? getSurveyTypeId(countryFilter, surveyFilter) : undefined;

    getFailedReports({
      variables: {
        input: {
          schoolIds,
          surveyType,
          startDate: startDate ? moment(startDate, 'MM.DD.YYYY').format('YYYY-MM-DD HH:mm:ss') : undefined,
          endDate: endDate ? moment(endDate, 'MM.DD.YYYY').format('YYYY-MM-DD HH:mm:ss') : undefined,
          userId: userFilter || undefined,
          excludeZeroResponses,
        },
      },
    });
  }, [countryFilter, schoolFilter, surveyFilter, startDate, endDate, userFilter, excludeZeroResponses, schoolAllOptions, getFailedReports, warningToast]);

  const handleSearchByCountry = useCallback(() => {
    if (!countryOnlyFilter) {
      warningToast('Please select a country');
      return;
    }

    getFailedReports({
      variables: {
        input: {
          countryCode: countryOnlyFilter,
          startDate: moment().subtract(30, 'days').format('YYYY-MM-DD HH:mm:ss'),
          endDate: moment().format('YYYY-MM-DD HH:mm:ss'),
          excludeZeroResponses,
        },
      },
    });
  }, [countryOnlyFilter, excludeZeroResponses, getFailedReports, warningToast]);

  const handleTriggerReport = useCallback(
    async (formId: string) => {
      try {
        setProcessingFormId(formId);
        const result = await manuallyTriggerReport({
          variables: {
            input: {
              formId,
            },
          },
        });

        if (result.data?.manuallyTriggerReport) {
          successToast('Report generation triggered successfully');
          
          // Refetch the failed reports to update the UI
          if (refetch) {
            await refetch();
          } else if (activeTab === 'byUser') {
            // If refetch is not available, perform the search again
            handleSearch();
          } else {
            handleSearchByCountry();
          }
        } else {
          errorToast('Failed to trigger report generation');
        }
      } catch (error) {
        logger.error('Error triggering report:', error);
        errorToast('An error occurred while triggering the report');
      } finally {
        setProcessingFormId(null);
      }
    },
    [manuallyTriggerReport, successToast, errorToast, refetch, activeTab, handleSearch, handleSearchByCountry]
  );

  // Function to copy text to clipboard
  const copyToClipboard = (text: string, type: 'email' | 'id' = 'email') => {
    navigator.clipboard.writeText(text)
      .then(() => {
        successToast(`${type === 'email' ? 'Email' : 'ID'} Copied Successfully!`);
      })
      .catch(err => {
        logger.error('Failed to copy text: ', err);
        errorToast(`Failed to copy ${type}`);
      });
  };

  // Format date from timestamp
  const formatDate = (timestamp: string | number) => {
    if (!timestamp) return '';
    
    try {
      // Convert to number if it's a string
      const timestampNum = typeof timestamp === 'string' ? parseInt(timestamp, 10) : timestamp;
      const date = moment(timestampNum);
      if (date.isValid()) {
        return date.format('MMM DD, YYYY HH:mm');
      }
      return 'Invalid date';
    } catch (error) {
      logger.error('Error formatting date:', error);
      return 'Date format error';
    }
  };

  const header: HeaderColumn[] = [
    { title: 'Type' },
    { title: 'School' },
    { title: 'Teacher' },
    { title: 'Email' },
    { title: 'Program' },
    { title: 'Lesson' },
    { title: 'Created At' },
    { title: 'Error' },
    { title: 'Attempts' },
    { title: 'Last Attempt' },
    { title: 'Responses' },
    { title: 'Actions' },
    { title: 'ID' },
  ];

  const body: BodyColumn[] = [
    {
      key: (record: any) => {
        switch (record.type) {
          case SURVEY_TYPE.EXIT_TICKET:
            return 'Exit Ticket';
          case SURVEY_TYPE.RESEARCH:
            return 'Research Survey (Not Supported)';
          case SURVEY_TYPE.NA_PRE_PROGRAM_QUIZ:
          case SURVEY_TYPE.AU_PRE_PROGRAM_QUIZ:
            return 'Pre-Program Quiz';
          case SURVEY_TYPE.NA_POST_PROGRAM_QUIZ:
          case SURVEY_TYPE.AU_POST_PROGRAM_QUIZ:
            return 'Post-Program Quiz';
          default:
            return `Type ${record.type}`;
        }
      },
      className: 'typeColumn'
    },
    { key: 'schoolName', className: 'schoolColumn' },
    { key: (record: any) => `${record.firstName || ''} ${record.lastName || ''}`, className: 'teacherColumn' },
    { 
      key: (record: any) => (
        <div className={styles.cellWithCopyIcon}>
          <div className={styles.cellContent} title={record.email}>
            <OverflowTooltip 
              text={record.email} 
              width={140} 
              placement="top"
            />
          </div>
          <div className={styles.iconGroup}>
            <ActionButtons
              tooltipPosition="top"
              showDelete={false}
              showView={false}
              showEdit={false}
              onCopy={() => copyToClipboard(record.email, 'email')}
              copyText={record.email}
              copyTooltip={`Copy email: ${record.email}`}
              rowColor="#FFB073"
            />
          </div>
        </div>
      ),
      className: 'emailColumn'
    },
    { key: 'programName', className: 'programColumn' },
    { key: 'lessonName', className: 'lessonColumn' },
    { 
      key: (record: any) => formatDate(record.createdAt),
      className: 'dateColumn'
    },
    { key: 'email_error' },
    { key: 'email_attempts' },
    {
      key: (record: any) => {
        if (!record.last_email_attempt) return '';
        return formatDate(record.last_email_attempt);
      }
    },
    { 
      key: 'feedbackFormCount',
      className: 'responsesColumn'
    },
    { 
      key: (record: any) => {
        // Disable the Retry button for research surveys (Type 2)
        const isResearchSurvey = record.type === SURVEY_TYPE.RESEARCH;
        
        return (
          <Button
            label="Retry"
            size="small"
            onClick={() => handleTriggerReport(record.id)}
            disabled={(isTriggering && processingFormId === record.id) || isResearchSurvey}
            leftIcon={isTriggering && processingFormId === record.id ? <Spinner width={16} height={16} /> : undefined}
            title={isResearchSurvey ? "Research surveys are not supported for manual triggering" : undefined}
          />
        );
      }
    },
    { 
      key: (record: any) => (
        <div className={styles.cellWithCopyIcon}>
          <div className={styles.cellContent} title={record.id}>
            <div style={{ textAlign: 'center' }}>
              <OverflowTooltip 
                text={record.id} 
                width={70} 
                placement="topRight"
              />
            </div>
          </div>
          <div className={styles.iconGroup}>
            <ActionButtons
              tooltipPosition="top"
              showDelete={false}
              showView={false}
              showEdit={false}
              onCopy={() => copyToClipboard(record.id, 'id')}
              copyText={record.id}
              copyTooltip={`Copy ID: ${record.id}`}
              rowColor="#FFB073"
            />
          </div>
        </div>
      ),
      className: 'idColumn'
    },
  ];

  // Filter section for "By User" tab using the reusable FilterSection component
  const userFilterSection = (
    <FilterSection
      searchValue=""
      onSearchChange={() => {}}
      hasSearchInput={false}
      showDivider={true}
      filters={[
        {
          value: countryFilter,
          onChange: handleChangeFilter(setCountryFilter, 'Country'),
          label: 'Country*',
          options: countryOptions,
          showSearch: false
        },
        {
          value: surveyFilter,
          onChange: handleChangeFilter(setSurveyFilter, 'SurveyType'),
          label: 'Survey Type',
          options: surveyTypeOptions
        },
        {
          value: accountFilter,
          onChange: handleChangeFilter(setAccountFilter, 'Account'),
          label: 'Account',
          options: customersOptions,
          showSearch: true,
          loading: isLoadingAccounts
        },
        {
          value: schoolFilter,
          onChange: handleChangeFilter(setSchoolFilter, 'School'),
          label: 'School',
          options: schoolOptions,
          showSearch: true,
          loading: isAccountSchoolsFetching,
          disabled: !accountFilter || isAccountSchoolsFetching
        },
        {
          value: userFilter,
          onChange: handleChangeFilter(setUserFilter, 'User'),
          label: 'User',
          options: schoolUsers,
          loading: isLoadingUsers,
          showSearch: true,
          disabled: !schoolFilter || isLoadingUsers
        }
      ]}
      actionButton={{
        label: 'Search',
        size: 'small',
        className: styles.searchBtn,
        onClick: handleSearch,
        disabled: isLoadingReports,
        color: 'orange'
      }}
      onReset={handleResetFilters}
      resultsCount={failedReportsData?.getFailedReports?.length}
      resultsCountLabel={`failed report${failedReportsData?.getFailedReports?.length !== 1 ? 's' : ''}`}
    >
      <DateRangeFilter
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={handleChangeDate(setStartDate)}
        onEndDateChange={handleChangeDate(setEndDate)}
        excludeZeroResponses={excludeZeroResponses}
        onExcludeZeroResponsesChange={() => setExcludeZeroResponses(!excludeZeroResponses)}
        startDateLabel="Start Date"
        endDateLabel="End Date"
      />
    </FilterSection>
  );

  // Filter section for "By Country" tab using the reusable FilterSection component
  const countryFilterSection = (
    <FilterSection
      searchValue=""
      onSearchChange={() => {}}
      hasSearchInput={false}
      showDivider={true}
      filters={[
        {
          value: countryOnlyFilter,
          onChange: handleChangeFilter(setCountryOnlyFilter, 'CountryOnly'),
          label: 'Country*',
          options: countryOptions,
          showSearch: false
        }
      ]}
      actionButton={{
        label: 'Search',
        size: 'small',
        className: styles.searchBtn,
        onClick: handleSearchByCountry,
        disabled: isLoadingReports,
        color: 'orange'
      }}
      onReset={() => setCountryOnlyFilter('')}
      advancedFilters={
        <Checkbox
          label="Exclude forms with zero responses"
          checked={excludeZeroResponses}
          onChange={() => setExcludeZeroResponses(!excludeZeroResponses)}
        />
      }
      resultsCount={failedReportsData?.getFailedReports?.length}
      resultsCountLabel={`failed report${failedReportsData?.getFailedReports?.length !== 1 ? 's' : ''}`}
    />
  );

  // Tab change handler
  const handleTabChange = (key: string) => {
    setActiveTab(key as 'byUser' | 'byCountry');
  };

  // Tabs component for the header
  const tabsComponent = (
    <Tabs
      activeKey={activeTab}
      onChange={handleTabChange}
      items={[
        {
          key: 'byUser',
          label: 'By User',
          children: null
        },
        {
          key: 'byCountry',
          label: 'By Country',
          children: null
        }
      ]}
    />
  );

  // Sort data by createdAt timestamp, newest first
  const sortedData = failedReportsData?.getFailedReports 
    ? [...failedReportsData.getFailedReports].sort((a, b) => {
        const aTime = parseInt(a.createdAt, 10);
        const bTime = parseInt(b.createdAt, 10);
        return bTime - aTime; // Descending order (newest first)
      })
    : [];

  // Get the appropriate no items text based on the active tab
  const noItemsText = activeTab === 'byCountry' && countryOnlyFilter
    ? `No failed reports found for ${countryOptions.find(option => option.value === countryOnlyFilter)?.label || countryOnlyFilter}.`
    : "No failed reports found.";

  return (
    <AdminGrid
      title="Manual Report Generation"
      subtitle={
        <div className={styles.subtitleContainer}>
          <div>This tool allows you to view and manually trigger report generation for forms that have failed to send emails.</div>
          <div className={styles.tabsContainer}>{tabsComponent}</div>
        </div>
      }
      filterSection={activeTab === 'byUser' ? userFilterSection : countryFilterSection}
      resultsCount={failedReportsData?.getFailedReports?.length}
      resultsCountLabel={`failed report${failedReportsData?.getFailedReports?.length !== 1 ? 's' : ''}`}
    >
      {failedReportsData?.getFailedReports && (
        <AdminTable
          header={header}
          body={body}
          data={sortedData}
          keyExtractor={(item) => item.id}
          loading={isLoadingReports}
          color="green"
          noItemsText={noItemsText}
        />
      )}
    </AdminGrid>
  );
};

export default ManualReportGeneration;
